import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { CityInput } from "@/app/Cities/Cities/Components/CityInput";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { createProDriverMutation } from "@/app/ProDrivers/Drivers/GraphQL/proDriverMutations";
import { LicenseTypeInput } from "@/app/ProDrivers/Drivers/Components/LicenseTypeInput";
import { TaxRegistrationInput } from "@/app/ProDrivers/Drivers/Components/TaxRegistrationInput";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import {
  CreateProDriverInput,
  ProDriverLicenseType,
  Region,
  TaxRegistration,
} from "@/gql/graphql";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { PaidDriverListItem } from "@/app/ProDrivers/Drivers/GraphQL/proDriverListQuery";
import { AllUserInput } from "@/app/Users/Components/AllUserInput";
import { useFormikContext } from "formik";
import { DialogComponent } from "@/lib/Components/Dialog/Hooks/useDialog";
import { PhoneInput } from "@/lib/Components/Form/Inputs/PhoneInput";

const validationSchema = z
  .object({
    user: z
      .object({
        connect: z.string().nullable(),
      })
      .optional(),
    name: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    basedIn: z.object({
      connect: z.string().min(1, "City is required"),
    }),
    bio: z.string().optional(),
    license_type: z.enum(ProDriverLicenseType),
  })
  .refine(
    (data) => {
      if (data.user?.connect) {
        return true;
      }
      return data.name && data.name.length > 0;
    },
    {
      message: "Name is required when not selecting an existing user",
      path: ["name"],
    },
  )
  .refine(
    (data) => {
      if (data.user?.connect) {
        return true;
      }
      return data.email && data.email.length > 0 && data.email.includes("@");
    },
    {
      message: "Valid email is required when not selecting an existing user",
      path: ["email"],
    },
  );

export type CreateProDriverDialogProps = {
  initialValues?: Partial<CreateProDriverInput>;
};

export const CreateProDriverDialog: DialogComponent<
  CreateProDriverDialogProps,
  PaidDriverListItem
> = ({ initialValues }) => {
  const { mutateAsync } = useGqlMutation(createProDriverMutation);
  const { close, resolve } = useDialogState<PaidDriverListItem>();

  return (
    <BaseDialog
      title="Add Pro Driver"
      subtitle="Select an existing user or create a new one"
    >
      <GenericForm<CreateProDriverInput>
        validationSchema={toFormikValidationSchema(validationSchema)}
        onSubmit={async (values) => {
          const input: CreateProDriverInput = {
            basedIn: values.basedIn,
            bio: values.bio || undefined,
            license_type: values.license_type,
            license_number: values.license_number || undefined,
            license_issuer: values.license_issuer || undefined,
            tax_registration: values.tax_registration,
            business_registration_number:
              values.business_registration_number || undefined,
          };

          if (values.user?.connect) {
            input.user = { connect: values.user?.connect };
          } else {
            input.name = values.name;
            input.email = values.email;
            input.phone = values.phone || undefined;
          }

          const res = await mutateAsync({
            input,
          });

          resolve?.(res.createProDriver as PaidDriverListItem);
          await close();
        }}
        initialValues={{
          user: {
            connect: null,
          },
          name: "",
          email: "",
          phone: "",
          basedIn: {
            connect: null,
          },
          bio: "",
          license_type: ProDriverLicenseType.Standard,
          license_number: "",
          license_issuer: "",
          tax_registration: TaxRegistration.None,
          business_registration_number: "",
          ...initialValues,
        }}
      >
        <CreateProDriverFormFields />
        <DialogSubmitButton text="Add Pro Driver" />
      </GenericForm>
    </BaseDialog>
  );
};

function CreateProDriverFormFields() {
  const { values } = useFormikContext<CreateProDriverInput>();
  const hasSelectedUser = !!values.user?.connect;

  return (
    <>
      <AllUserInput
        name="user.connect"
        label="Select Existing User"
        className="col-span-full"
        clearable
      />

      {!hasSelectedUser ? (
        <>
          <div className="col-span-full border-t pt-4">
            <p className="text-sm text-gray-500">
              Or enter details to create a new user:
            </p>
          </div>
          <TextInput name="name" label="Full Name" className="col-span-full" />
          <TextInput
            name="email"
            label="Email"
            type="email"
            className="col-span-4"
          />
          <PhoneInput
            name="phone"
            label="Phone"
            className="col-span-4"
            defaultRegion={Region.Au}
          />
        </>
      ) : null}

      <div className="col-span-full border-t pt-4">
        <p className="text-sm font-medium text-gray-700">Driver Details</p>
      </div>
      <CityInput name="basedIn.connect" label="Based In" />
      <LicenseTypeInput label="License Type" name="license_type" />
      <TextInput
        name="license_number"
        label="License Number"
        className="col-span-3"
      />
      <TextInput
        name="license_issuer"
        label="License Issuer"
        className="col-span-3"
      />
      <TaxRegistrationInput
        name="tax_registration"
        label="Tax Registration"
        className="col-span-3"
      />
      {values.tax_registration !== TaxRegistration.None ? (
        <TextInput
          name="business_registration_number"
          label="Business Registration Number"
          className="col-span-3"
        />
      ) : null}
      <TextAreaInput name="bio" label="Bio" className="col-span-full" />
    </>
  );
}
